import React, { useEffect } from 'react';
import { Switch, Redirect, useParams } from 'react-router-dom';

import { SecuredRoute } from 'screens';
import { ProgressBar } from './components/ProgressBar';

import gql from 'graphql-tag';
import { trackingOnboardingStart, getFlag, flags } from 'services';
import { useQuery } from '@apollo/react-hooks';
import { useSaveOnboarding } from './hooks/useSaveOnboarding';

import niraLogo from '../../assets/img/app-logo.svg';
import styles from './OnboardingScreen.module.scss';

import { ONBOARDING_FLOWS } from './config';

import {
  ConnectApps,
  AddYourTeam,
  SetupOrganization,
  AddToChrome,
  FinishOnboarding,
} from './components';

import { LoaderCode } from './components/LoaderCode';
import { OrganizationTypes } from 'helpers';
import { isNewOrganization } from 'helpers/organizations';
import { Loader } from 'components';

interface OnboardingScreenData {
  me: {
    userProfile: User;
    organization: Organization;
  };
}

const getUserProfileQuery = gql`
  query getUserProfile {
    me {
      userProfile {
        id
        name
        avatarUrl
        onboardingStep
      }
      organization {
        id
        name
        type
        memberCount
      }
    }
  }
`;

export const OnboardingScreen = () => {
  const forceOnboarding = getFlag(flags.ForceOnboarding);
  const { step } = useParams();
  const { error, data } = useQuery<OnboardingScreenData>(getUserProfileQuery, {
    fetchPolicy: 'cache-and-network',
  });

  useSaveOnboarding(data?.me.organization.id, step);

  useEffect(() => {
    trackingOnboardingStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (data === undefined) return null;

  if (error) return <Loader color="Red" />;

  /**
   * Matches the last visited step from the server to an onboarding route
   * If it has no value it will go to the LoaderCode
   * Legacy values here for backwards compatibility
   */
  const getLastVisitedStep = () => {
    const { onboardingStep } = data.me.userProfile;

    switch (onboardingStep) {
      case 'connect':
      case 'connect-apps':
        return 'connect-apps';
      case 'setup':
      case 'setup-organization':
        return 'setup-organization';
      case 'add-team':
        return 'add-team';
      case 'chrome':
      case 'add-to-chrome':
        return 'add-to-chrome';
      case 'finished':
      case 'finish':
        return 'finish';
      default:
        return 'start';
    }
  };

  const firstStep = forceOnboarding ? 'start' : getLastVisitedStep();

  // If there's no step in the url redirect the user to /start
  if (!step) {
    return <Redirect to={`/onboarding/${firstStep}`} />;
  }

  // If the url step is /start, launch the loader code animation
  if (step === 'start') {
    return <LoaderCode />;
  }

  const isNewOrg = isNewOrganization(data.me.organization);
  const isWorkAccount = data.me.organization.type === OrganizationTypes.WORK;

  /**
   * Return the total ammount of steps that the
   * onboarding will have for the current user
   */
  const getOnboardingFlow = (): { paths: string[] } => {
    // New Work Account
    if (isWorkAccount && isNewOrg) {
      return ONBOARDING_FLOWS.workNewOrg;
    }

    // Existing Work Account
    if (isWorkAccount) {
      return ONBOARDING_FLOWS.workExistingOrg;
    }

    // Personal Account
    return ONBOARDING_FLOWS.personal;
  };

  /**
   * Returns a number corresponding to the current step in the onboarding process
   */
  const getCurrentStep = (): number => {
    return step ? getOnboardingFlow().paths.indexOf(step) + 1 : 0;
  };

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.logo}>
            <img src={niraLogo} alt="Logo" />
        </div>

        <ProgressBar
          className={styles.progressBar}
          currentStep={getCurrentStep()}
          totalSteps={getOnboardingFlow().paths.length}
          type="Node"
        />

        <img
          alt={data.me.userProfile.name}
          src={data.me.userProfile.avatarUrl}
          className={styles.avatar}
        />

        <Switch>
          <SecuredRoute exact path="/onboarding/connect-apps" component={ConnectApps} />
          <SecuredRoute exact path="/onboarding/setup-organization" component={SetupOrganization} />
          <SecuredRoute exact path="/onboarding/add-team" component={AddYourTeam} />
          <SecuredRoute exact path="/onboarding/add-to-chrome" component={AddToChrome} />
          <SecuredRoute exact path="/onboarding/finish" component={FinishOnboarding} />
          <Redirect to="/onboarding/start" />
        </Switch>
      </div>
    </div>
  );
};

export default OnboardingScreen;
